import classNames from "classnames";
import { useCallback, useState } from "react";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";
import styles from "./AscFindTheWordsPuzzle.module.scss";
import AscFindTheWordsPuzzleBoard from "./AscFindTheWordsPuzzleBoard/AscFindTheWordsPuzzleBoard";
import {
  AscFindTheWordsPuzzleBoardSize,
  AscFindTheWordsPuzzleWord,
  AscFindTheWordsPuzzleProps,
} from "@ascendnow/shared";
import AscFindTheWordsPuzzleClueList from "./AscFindTheWordsPuzzleClueList/AscFindTheWordsPuzzleClueList";

export default function AscFindTheWordsPuzzle({
  className,
  words,
  generatedWords,
  solvedWords,
  mode = "solve",
  size = AscFindTheWordsPuzzleBoardSize.TEN_BY_TEN,
  disabled = false,
  onGenerate,
  onSolve,
  onError,
}: AscFindTheWordsPuzzleProps) {
  const [createModeSize, setCreateModeSize] =
    useState<AscFindTheWordsPuzzleBoardSize>(size);
  const [placedWords, setPlacedWords] = useState<AscFindTheWordsPuzzleWord[]>();
  const [foundWords, setFoundWords] = useState<AscFindTheWordsPuzzleWord[]>();
  function handleOnChange(words: AscFindTheWordsPuzzleWord[]) {
    setFoundWords(words);
    onSolve?.(words);
  }

  function handleOnSizeChange(size?: AscFindTheWordsPuzzleBoardSize) {
    if (!size) {
      return;
    }
    setCreateModeSize(size);
  }

  const handleOnGenerate = useCallback(
    (
      words: AscFindTheWordsPuzzleWord[],
      size: AscFindTheWordsPuzzleBoardSize
    ) => {
      if (disabled) {
        return;
      }
      setPlacedWords(words);
      onGenerate?.(words, size);
    },
    [disabled, onGenerate]
  );

  const handleOnError = useCallback(
    (error: string) => {
      onError?.(error);
    },
    [onError]
  );

  return (
    <div
      data-testid="asc-find-the-words-puzzle"
      className={classNames(styles.ground, className)}
    >
      <AscFindTheWordsPuzzleBoard
        mode={mode}
        words={words}
        generatedWords={generatedWords}
        solvedWords={solvedWords}
        onChange={handleOnChange}
        onGenerate={handleOnGenerate}
        size={mode === "create" ? createModeSize : size}
        disabled={disabled}
        onError={handleOnError}
        className={styles.board}
      />
      <div className={styles.infoContainer}>
        {mode === "create" ? (
          <AscSelectInput
            value={size}
            multiple={false}
            onChange={handleOnSizeChange}
            className={styles.sizeSelector}
          >
            <AscSelectInput.Option
              value={AscFindTheWordsPuzzleBoardSize.SIX_BY_SIX}
              label="6 x 6"
            />
            <AscSelectInput.Option
              value={AscFindTheWordsPuzzleBoardSize.EIGHT_BY_EIGHT}
              label="8 x 8"
            />
            <AscSelectInput.Option
              value={AscFindTheWordsPuzzleBoardSize.TEN_BY_TEN}
              label="10 x 10"
            />
            <AscSelectInput.Option
              value={AscFindTheWordsPuzzleBoardSize.TWELVE_BY_TWELVE}
              label="12 x 12"
            />
          </AscSelectInput>
        ) : null}
        {mode === "create" ? (
          <AscFindTheWordsPuzzleClueList words={placedWords || []} />
        ) : (
          <AscFindTheWordsPuzzleClueList
            words={generatedWords || []}
            foundWords={foundWords}
          />
        )}
      </div>
    </div>
  );
}
